import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { DashboardConfig, saveUserPerferences } from '../../dispatchers/UserPreferenceDispatcher';
import { fetchColumnsData } from '../../dispatchers/ColumnSelectorDispatcher';
import { fetchWellTestId } from '../../dispatchers/WellTestDispatcher';
import SuccessfullToast from '../common/toast/SuccessfullToast';
import ErrorToast from '../common/toast/ErrorToast';
// import TaskDetailsWidget from '../../components/task-details-widget/TaskDetailsWidget';
import WellNotesWidget from '../../components/well-notes-widget/WellNotesWidget';
import { updateTaskTable } from '../../components/slices/ColumnSelectorSlice';
import { useParams } from 'react-router-dom';
import { setIndustryApplicationId } from '../asset-analysis/AssetAnalysisSlice';
import Loader from '../common/Loader/Loader';
// import { AccessiableWidget } from '../../models/LicenseWidget';
// import useConfig from '../../hooks/useConfig';
import { R, WIDGETS } from '../../rbac';
import { ProtectComponent } from '../../rbac';

// Define interfaces for better type safety
interface ColumnObject {
    key: string;
    label: string;
    selected: boolean;
    order: number;
}

export default function WellComments() {
    const dispatch = useAppDispatch();
    const { assetId } = useParams<{ assetId: string }>();

    const chartType = 'notes';
    const pumpCheckerPageData = useAppSelector((state: any) => state.testWellColumnsSlice.data?.widgets);
    const industryApplicationId = useAppSelector((state) => state.AssetAnalysis.industryApplicationId);
    const [isESP, setESP] = useState([7, 4].includes(Number(industryApplicationId)));
    const wellId = useAppSelector((state) => state.WellTestIdSlice?.data?.WellId);
    // State for task refresh trigger
    const [taskRefreshTrigger, setTaskRefreshTrigger] = useState(false);

    // State for well notes refresh trigger
    const [wellNotesRefreshTrigger] = useState(false);

    useEffect(() => {
        industryApplicationId && setESP([7, 4].includes(industryApplicationId));
    }, [industryApplicationId]);
    // Direct chartType initialization - since this component is used in ESP context, set ESP chartType immediately
    useEffect(() => {
        if (assetId && (!chartType || chartType.trim() === '')) {
            console.log('WellNotesTasks: Directly setting ESP chartType for assetId:', assetId);
            dispatch(
                setIndustryApplicationId({
                    industryApplicationId: 4, // ESP type
                    currentTab: 'notesAndTasks',
                }),
            );
        }
    }, [dispatch, assetId, chartType]);

    // Fetch columns data to populate pumpCheckerPageData (required for TaskDetailsWidget)
    useEffect(() => {
        if (chartType && chartType.trim() !== '') {
            console.log('WellNotesTasks: Fetching columns data for chartType:', chartType);
            dispatch(fetchColumnsData(chartType));
        }
    }, [dispatch, chartType]);

    // Initialize well test data (from WellChart.tsx pattern) - but only after chartType is set
    useEffect(() => {
        if (assetId && chartType && chartType.trim() !== '') {
            console.log('WellNotesTasks: Fetching well test ID for assetId:', assetId, 'with chartType:', chartType);
            dispatch(fetchWellTestId(assetId));
        }
    }, [dispatch, assetId, chartType]);

    // Trigger initial data fetch when component mounts and wellId is available
    useEffect(() => {
        if (wellId && wellId > 0) {
            console.log('WellNotesTasks: Triggering task refresh for wellId:', wellId);
            setTaskRefreshTrigger((prev) => !prev);
        }
    }, [wellId]);

    // Debug current state
    useEffect(() => {
        console.log(
            'WellNotesTasks: Current state - assetId:',
            assetId,
            'wellId:',
            wellId,
            'chartType:',
            chartType,
            'taskRefreshTrigger:',
            taskRefreshTrigger,
        );
        console.log('WellNotesTasks: pumpCheckerPageData:', pumpCheckerPageData);
        console.log('WellNotesTasks: Component initialization order check');
    }, [assetId, wellId, chartType, taskRefreshTrigger, pumpCheckerPageData]);

    // Debug when chartType changes
    useEffect(() => {
        console.log('WellNotesTasks: chartType changed to:', chartType);
        if (chartType && chartType.trim() !== '') {
            console.log('WellNotesTasks: chartType is now available, proceeding with data fetch');
        }
    }, [chartType]);

    // Debug pumpCheckerPageData and check for taskDetails widget
    useEffect(() => {
        if (pumpCheckerPageData) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            console.log(
                'WellNotesTasks: pumpCheckerPageData available with widgets:',
                pumpCheckerPageData.map((w: any) => w.key),
            );
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const taskDetailsWidget = pumpCheckerPageData.find((obj: any) => obj.key === 'taskDetails');
            console.log('WellNotesTasks: taskDetails widget found:', !!taskDetailsWidget);
            if (taskDetailsWidget) {
                console.log('WellNotesTasks: taskDetails widget config:', taskDetailsWidget);
            }
        }
    }, [pumpCheckerPageData]);

    // State for toast messages
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [saveConfigResponse, setSaveConfigResponse] = useState(false);
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const [showErrorToast, setShowErrorToast] = useState(false);

    // Update toast display logic
    useEffect(() => {
        if (saveConfigResponse && successMessage) {
            setShowSuccessToast(true);
        }
    }, [saveConfigResponse, successMessage]);

    useEffect(() => {
        if (errorMessage) {
            setShowErrorToast(true);
        }
    }, [errorMessage]);

    //   // Task handling functions - duplicated from WellChart.tsx

    // Update columns helper function
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updateColumns = (updatedColumns: any[], existingColumns: ColumnObject[]) => {
        updatedColumns.forEach((updatedCol) => {
            const existingCol = existingColumns.find((col) => col.key === updatedCol.Key);
            if (existingCol) {
                existingCol.selected = updatedCol.Selected;
                existingCol.order = updatedCol.Order;
            }
        });
        existingColumns.sort((a, b) => a.order - b.order);
    };

    const handleApplyClicked = async (source: string, columns: ColumnObject[], isConfigurationChecked: boolean) => {
        if (source === 'taskTable') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const taskDetails = pumpCheckerPageData?.find((obj: any) => obj.key === 'taskDetails');
            if (!taskDetails) return;
            if (taskDetails) {
                try {
                    const orderedKeys: string[] = columns && columns.length > 0 ? columns.map((obj) => obj.key) : [];

                    const allSortedColumns = [...columns].sort((a, b) => {
                        const indexA = orderedKeys.indexOf(a.key);
                        const indexB = orderedKeys.indexOf(b.key);
                        return (indexA !== -1 ? indexA : Infinity) - (indexB !== -1 ? indexB : Infinity);
                    });

                    const visibleKeys = columns.filter((obj) => obj.selected).map((obj) => obj.key);
                    const updatedColumns = allSortedColumns.map((col, index) => ({
                        Order: index + 1,
                        Label: col.label,
                        Key: col.key,
                        Selected: visibleKeys ? visibleKeys.includes(col.key) : [],
                        Visible: true,
                    }));
                    const transformedTaskTableWidget: DashboardConfig = {
                        DashboardName: taskDetails?.widgetProperty?.dashboard ?? '',
                        WidgetName: taskDetails?.widgetProperty?.key ?? '',
                        PropertyType: 'Columns',
                        Preferences: updatedColumns,
                    };
                    if (isConfigurationChecked) {
                        const resultAction = await dispatch(saveUserPerferences(transformedTaskTableWidget));
                        if (typeof resultAction.payload === 'boolean') {
                            if (taskDetails?.widgetProperty && taskDetails?.widgetProperty?.isDefault) {
                                const clonedtaskTableWidget = JSON.parse(JSON.stringify(taskDetails));

                                clonedtaskTableWidget.widgetProperty.isDefault = false;

                                updateColumns(updatedColumns, clonedtaskTableWidget.widgetProperty.widgetProperties.columns);
                                dispatch(updateTaskTable(clonedtaskTableWidget));
                            }

                            setSaveConfigResponse(resultAction.payload);
                            if (resultAction.payload) {
                                setSuccessMessage('Successfully Saved Task Table Configuration');
                                setTimeout(() => {
                                    setSaveConfigResponse(false);
                                }, 3000);
                            } else {
                                setErrorMessage('Error saving user preferences.');
                            }
                        } else {
                            setSaveConfigResponse(false);
                            setErrorMessage('Unexpected response from the server.');
                        }
                    }
                } catch (error) {
                    console.error('Unexpected error during dispatch:', error);
                    setErrorMessage('Unexpected error occurred. Please try again saving user preference.');
                }
            }
        } else if (source === 'wellNotesTable') {
            // Handle well notes table configuration
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const wellNotesDetails = pumpCheckerPageData?.find((obj: any) => obj.key === 'well-notes');
            if (!wellNotesDetails) return;

            try {
                const orderedKeys: string[] = columns && columns.length > 0 ? columns.map((obj) => obj.key) : [];

                const allSortedColumns = [...columns].sort((a, b) => {
                    const indexA = orderedKeys.indexOf(a.key);
                    const indexB = orderedKeys.indexOf(b.key);
                    return (indexA !== -1 ? indexA : Infinity) - (indexB !== -1 ? indexB : Infinity);
                });

                const visibleKeys = columns.filter((obj) => obj.selected).map((obj) => obj.key);
                const updatedColumns = allSortedColumns.map((col, index) => ({
                    Order: index + 1,
                    Label: col.label,
                    Key: col.key,
                    Selected: visibleKeys ? visibleKeys.includes(col.key) : [],
                    Visible: true,
                }));

                const transformedWellNotesWidget: DashboardConfig = {
                    DashboardName: wellNotesDetails?.widgetProperty?.dashboard ?? '',
                    WidgetName: wellNotesDetails?.widgetProperty?.key ?? '',
                    PropertyType: 'Columns',
                    Preferences: updatedColumns,
                };

                if (isConfigurationChecked) {
                    const resultAction = await dispatch(saveUserPerferences(transformedWellNotesWidget));
                    if (typeof resultAction.payload === 'boolean') {
                        setSaveConfigResponse(resultAction.payload);
                        if (resultAction.payload) {
                            setSuccessMessage('Successfully Saved Well Notes Configuration');
                            setTimeout(() => {
                                setSaveConfigResponse(false);
                            }, 3000);
                        } else {
                            setErrorMessage('Error saving well notes preferences.');
                        }
                    } else {
                        setSaveConfigResponse(false);
                        setErrorMessage('Unexpected response from the server.');
                    }
                }
            } catch (error) {
                console.error('Unexpected error during well notes dispatch:', error);
                setErrorMessage('Unexpected error occurred. Please try again saving well notes preference.');
            }
        }
    };

    return (
        <div>
            {/* Well Notes Widget - Only render when required data is available */}
            {assetId && chartType && pumpCheckerPageData && (
                <ProtectComponent resource={R.widget(chartType, WIDGETS.WELL_COMMENTS)} action='view' widgetName='Well Comments'>
                    <div>
                        <WellNotesWidget
                            wellId={wellId}
                            dashboardName={chartType}
                            wellNotesRefreshTrigger={wellNotesRefreshTrigger}
                            handleApplyClickedWellNotesWidget={(columns: ColumnObject[], isConfigurationChecked: boolean) =>
                                handleApplyClicked('wellNotesTable', columns, isConfigurationChecked)
                            }
                            isESP={isESP}

                        />
                    </div>
                </ProtectComponent>
            )}

            {/* Show loader while data is loading */}
            {(!assetId || !chartType || !pumpCheckerPageData) && <Loader />}

            {/* Toast Messages */}
            <SuccessfullToast
                message={successMessage}
                isShowEnabled={showSuccessToast}
                callback={() => {
                    setSuccessMessage('');
                    setShowSuccessToast(false);
                    setSaveConfigResponse(false);
                }}
            />
            <ErrorToast
                message={errorMessage}
                isShowEnabled={showErrorToast}
                callback={() => {
                    setErrorMessage('');
                    setShowErrorToast(false);
                }}
            />
        </div>
    );
}
